// config/clickupConfig.js - ClickUp API Configuration

// ClickUp API Configuration
// To get your API token: https://app.clickup.com/settings/apps
// To get your List ID: Open your ClickUp list, the ID is in the URL
// Example URLs:
//   https://app.clickup.com/123456/v/li/987654321  -> List ID: 987654321
//   https://app.clickup.com/90182481018/v/l/2kzmpx3u-518  -> List ID: 2kzmpx3u-518
// List IDs can be numeric or alphanumeric (with hyphens)

// Helper to get and trim environment variable
const getEnvVar = (key, defaultValue = '') => {
  const value = process.env[key];
  return value ? value.trim() : defaultValue;
};

export const CLICKUP_CONFIG = {
  // Your ClickUp API Token (get from: https://app.clickup.com/settings/apps)
  // Token should start with 'pk_' for personal tokens
  API_TOKEN: getEnvVar('REACT_APP_CLICKUP_API_TOKEN'),

  // Your ClickUp List ID where feedback tasks will be created (default/fallback)
  // Get this from your ClickUp list URL
  LIST_ID: getEnvVar('REACT_APP_CLICKUP_LIST_ID'),

  // Separate List IDs for different reason types (optional)
  // If set, tasks will be routed directly to the appropriate list based on reason
  // Get List IDs from your ClickUp list URLs
  LIST_IDS: {
    GENERAL_QUERY: getEnvVar('REACT_APP_CLICKUP_LIST_ID_GENERAL_QUERY'),
    REPORTING_ISSUE: getEnvVar('REACT_APP_CLICKUP_LIST_ID_REPORTING_ISSUE'),
    REQUESTING_FEATURE: getEnvVar('REACT_APP_CLICKUP_LIST_ID_REQUESTING_FEATURE'),
  },

  // Support ticket assignees (ClickUp user emails). Comma-separated.
  // Change anytime: add/remove emails, restart React, next tickets use the new list.
  // Default: shared support mailbox only (avoids duplicate “you” + “AQ Team” emails).
  ASSIGNEE_EMAILS: getEnvVar(
    'REACT_APP_CLICKUP_ASSIGNEE_EMAILS',
    'aqdev@aq-trials.com',
  ),

  // Optional comma-separated ClickUp numeric user IDs (fallback / override).
  ASSIGNEE_USER_IDS: getEnvVar('REACT_APP_CLICKUP_ASSIGNEE_USER_IDS'),

  // Optional: also assign a User Group (e.g. AQ Team). Leave empty to avoid
  // duplicate notifications when the same people are already in ASSIGNEE_EMAILS.
  GROUP_ASSIGNEE_ID: getEnvVar('REACT_APP_CLICKUP_GROUP_ASSIGNEE_ID'),
  GROUP_ASSIGNEE_NAME: getEnvVar('REACT_APP_CLICKUP_GROUP_ASSIGNEE_NAME', ''),

  // ClickUp API Base URL
  API_BASE_URL: 'https://api.clickup.com/api/v2',

  // Default task settings
  DEFAULT_PRIORITY: 3, // 1 = urgent, 2 = high, 3 = normal, 4 = low
  DEFAULT_STATUS: 'to do', // Initial status for new tasks

  // Note: Custom fields are auto-detected from ClickUp
  // Make sure you have these custom fields in your ClickUp list:
  // - "User Name" (Short Text)
  // - "User Email" (Email)
  // - "Organization" (Short Text)
  // - "Reason" (Dropdown/Text)
};

// Helper function to get ClickUp API headers
export const getClickUpHeaders = () => {
  const token = CLICKUP_CONFIG.API_TOKEN?.trim();
  
  if (!token) {
    throw new Error('ClickUp API token is not configured. Please set REACT_APP_CLICKUP_API_TOKEN environment variable in your .env file.');
  }

  // ClickUp API requires the token directly in Authorization header (no "Bearer" prefix)
  // Personal tokens start with "pk_"
  if (!token.startsWith('pk_')) {
    console.warn('Warning: ClickUp token should start with "pk_" for personal tokens. Current token starts with:', token.substring(0, 3));
  }

  return {
    'Authorization': token,
    'Content-Type': 'application/json',
  };
};

// Helper function to get full ClickUp API URL
export const getClickUpUrl = (endpoint) => {
  return `${CLICKUP_CONFIG.API_BASE_URL}${endpoint}`;
};

export default CLICKUP_CONFIG;
